<script setup>
import { useDashboard } from '@/composables/useDashboard'

// pages/dashboards/index.vue — Module 20 (§27). Menggantikan
// dashboards/analytics.vue (placeholder Phase-0), sekarang menampilkan data
// real dari seluruh Core Engine, difilter otomatis sesuai role (BR-011) —
// audience (utama/direksi/tim-mutu/unit) 100% ditentukan backend
// (DashboardService::widgetsFor()), halaman ini murni menampilkan apa pun
// yang dikembalikan.
definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'Dashboard',
  },
})

const { widgets, loading, fetchDashboard } = useDashboard()

const selectedUnit = ref(null)
const expandedIndicator = ref(null)

const load = () => fetchDashboard(selectedUnit.value)

onMounted(load)

const audienceLabel = computed(() => ({
  utama: 'Dashboard Utama',
  direksi: 'Dashboard Direksi',
  'tim-mutu': 'Dashboard Tim Mutu',
  unit: 'Dashboard Unit',
}[widgets.value?.audience] ?? 'Dashboard'))

const toggleTrend = indicatorId => {
  expandedIndicator.value = expandedIndicator.value === indicatorId ? null : indicatorId
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between flex-wrap gap-3 mb-4">
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">
          {{ audienceLabel }}
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Data real-time, hanya baca, difilter otomatis sesuai peran Anda (BR-011)
        </p>
      </div>
    </div>

    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <template v-else-if="widgets">
      <!-- Audience: unit — perlu pilih unit dulu (tidak ada kolom unit_id di users, lihat CLAUDE.md) -->
      <VCard
        v-if="widgets.audience === 'unit' && !widgets.unit_selected"
        class="mb-4"
      >
        <VCardText>
          <p class="text-body-1 mb-3">
            Pilih unit Anda untuk menampilkan data dashboard.
          </p>
          <VRow>
            <VCol
              cols="12"
              md="4"
            >
              <MasterDataPicker
                v-model="selectedUnit"
                entity="units"
                label="Unit Saya"
                @update:model-value="load"
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <template v-else>
        <VRow
          v-if="widgets.stats"
          class="mb-2"
        >
          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <WidgetCard
              title="Dokumen Terbit"
              :value="widgets.stats.documents_published"
              icon="ri-file-text-line"
              color="primary"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <WidgetCard
              title="IKP Terbuka"
              :value="widgets.stats.incidents_open"
              icon="ri-alarm-warning-line"
              color="error"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <WidgetCard
              title="CAPA Terbuka"
              :value="widgets.stats.capas_open"
              :subtitle="`${widgets.stats.capas_overdue} terlambat`"
              icon="ri-task-line"
              color="warning"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <WidgetCard
              title="Reminder Jatuh Tempo"
              :value="widgets.stats.reminders_due"
              icon="ri-notification-3-line"
              color="info"
            />
          </VCol>
        </VRow>

        <VRow
          v-if="widgets.audience === 'unit' && widgets.unit_selected"
          class="mb-2"
        >
          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <WidgetCard
              title="IKP Terbuka (Unit)"
              :value="widgets.open_incidents"
              icon="ri-alarm-warning-line"
              color="error"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <WidgetCard
              title="CAPA Terbuka (Unit)"
              :value="widgets.open_capas"
              icon="ri-task-line"
              color="warning"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <WidgetCard
              title="Kepatuhan Checklist (30 hari)"
              :value="widgets.checklist_compliance_rate !== null ? `${widgets.checklist_compliance_rate}%` : '-'"
              icon="ri-checkbox-multiple-line"
              color="success"
            />
          </VCol>
        </VRow>

        <VRow v-if="widgets.checklist_compliance_rate !== undefined && widgets.audience !== 'unit'">
          <VCol
            cols="12"
            md="4"
          >
            <WidgetCard
              title="Kepatuhan Checklist (30 hari)"
              :value="widgets.checklist_compliance_rate !== null ? `${widgets.checklist_compliance_rate}%` : '-'"
              icon="ri-checkbox-multiple-line"
              color="success"
            />
          </VCol>
        </VRow>

        <VRow class="mt-2">
          <VCol
            v-if="widgets.indicators ?? widgets.indicator_summary"
            cols="12"
            md="7"
          >
            <VCard>
              <VCardTitle class="pa-6 pb-0">
                Indikator Mutu
              </VCardTitle>
              <VCardText class="pa-6">
                <VList>
                  <template
                    v-for="ind in (widgets.indicators ?? widgets.indicator_summary)"
                    :key="ind.id ?? ind.name"
                  >
                    <VListItem
                      :title="ind.name"
                      :subtitle="`${ind.unit_name ?? ''} — Target: ${ind.target_value ?? '-'}`"
                      @click="ind.id && toggleTrend(ind.id)"
                    >
                      <template #append>
                        <VChip
                          size="small"
                          color="primary"
                          variant="tonal"
                        >
                          {{ ind.latest_value ?? '-' }}
                        </VChip>
                      </template>
                    </VListItem>
                    <div
                      v-if="expandedIndicator === ind.id && ind.assessment_template_id"
                      class="px-4 pb-4"
                    >
                      <TrendChart
                        :template-id="ind.assessment_template_id"
                        subject-type="unit"
                        :subject-id="ind.unit_id"
                      />
                    </div>
                  </template>
                  <VListItem v-if="!(widgets.indicators ?? widgets.indicator_summary)?.length">
                    <VListItemTitle class="text-medium-emphasis">
                      Belum ada indikator aktif.
                    </VListItemTitle>
                  </VListItem>
                </VList>
              </VCardText>
            </VCard>
          </VCol>

          <VCol
            cols="12"
            :md="widgets.indicators ?? widgets.indicator_summary ? 5 : 12"
          >
            <VCard class="mb-4">
              <VCardTitle class="pa-6 pb-0">
                Reminder Mendatang
              </VCardTitle>
              <VCardText class="pa-6">
                <VList density="compact">
                  <VListItem
                    v-for="(r, idx) in widgets.reminders_upcoming"
                    :key="idx"
                    :title="`${r.remindable_type}${r.context ? ' (' + r.context + ')' : ''}`"
                    :subtitle="`Jatuh tempo: ${r.due_date}`"
                  />
                  <VListItem v-if="!widgets.reminders_upcoming?.length">
                    <VListItemTitle class="text-medium-emphasis">
                      Tidak ada reminder mendatang.
                    </VListItemTitle>
                  </VListItem>
                </VList>
              </VCardText>
            </VCard>

            <VCard v-if="widgets.ikp_by_status">
              <VCardTitle class="pa-6 pb-0">
                IKP per Status
              </VCardTitle>
              <VCardText class="pa-6">
                <VChip
                  v-for="(total, status) in widgets.ikp_by_status"
                  :key="status"
                  class="ma-1"
                  variant="tonal"
                >
                  {{ status }}: {{ total }}
                </VChip>
                <p
                  v-if="!Object.keys(widgets.ikp_by_status ?? {}).length"
                  class="text-medium-emphasis mb-0"
                >
                  Belum ada data IKP.
                </p>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>

        <VRow v-if="widgets.capa_overdue?.length">
          <VCol cols="12">
            <VCard>
              <VCardTitle class="pa-6 pb-0">
                CAPA Terlambat
              </VCardTitle>
              <VCardText class="pa-6">
                <VList density="compact">
                  <VListItem
                    v-for="(c, idx) in widgets.capa_overdue"
                    :key="idx"
                    :title="c.title"
                    :subtitle="`PIC: ${c.pic_name ?? '-'} — Jatuh tempo: ${c.due_date}`"
                  />
                </VList>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </template>
    </template>
  </div>
</template>
